import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import {
  HeaderBlock,
  Expander,
  ExpanderContent,
  Button,
  useContainerWidth,
  Theme,
  Separator,
  Grid,
  Cell,
  WhiteText,
  BillboardButton,
} from '@fs/zion-ui'
import ZionForm, { useZionForm, FormTextField, useZionFormContext } from '@fs/zion-form'
import { ArrowCaret } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import { trackEvent } from '@fs/zion-analytics'
import type { SearchParams } from '../../types/data-types'
import EventFields from './EventFields'
import type { Gender } from '../../types/persons'

const inputContainerCss = css`
  text-align: left;
`

const nameFieldSwapLocalesRegEx = /^ja|^km|^ko|^mn|^ro|^to|^vi|^zh/

type SearchButtonProps = {
  gender: Gender | undefined
  'data-testid': string
}

function SearchButton({ 'data-testid': dataTestId, gender }: SearchButtonProps): JSX.Element {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ md: false, default: true })
  const { formState } = useZionFormContext()
  const { isValid, isDirty } = formState
  return (
    <WhiteText>
      <BillboardButton
        type="submit"
        emphasis="lightHigh"
        fullWidth={isMobile}
        disabled={!isValid || !isDirty}
        data-testid={dataTestId}
        color={gender === 'FEMALE' ? 'red' : 'blue'}
      >
        {t('common-ui:search.action')}
      </BillboardButton>
    </WhiteText>
  )
}

export default function Search({
  header,
  params,
  onSubmit,
  showMore,
  onSearchPage = false,
  gender,
}: {
  header?: string | undefined
  params?: SearchParams
  onSubmit?: (values: SearchParams) => void
  showMore?: boolean
  onSearchPage?: boolean
  gender?: Gender
}): JSX.Element {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ md: false, default: true })
  const methods = useZionForm({
    mode: 'all',
    resolver: validate,
    defaultValues: params,
  })
  const swapNames = nameFieldSwapLocalesRegEx.test(i18n.language)
  const [open, setOpen] = useState(false)

  function validate(values: SearchParams): { values: unknown; errors: unknown } {
    const error = !values.self?.surname?.value
    return {
      values: error ? {} : values,
      errors: error ? { self: { surname: { value: { message: t('ancestors.fields.required') } } } } : {},
    }
  }

  function handleSubmit(values: SearchParams): void {
    if (onSubmit) {
      onSubmit(values)
    }
    trackEvent({ link_name: 'Search' })
  }

  return (
    <div css={inputContainerCss}>
      <ZionForm methods={methods} onSubmit={handleSubmit}>
        {header && <HeaderBlock centered size="sm" heading={header} />}

        <NameFields swapNames={swapNames} onSearchPage={onSearchPage} gender={gender} />

        {showMore && (
          <>
            <Expander open={open} setOpen={setOpen}>
              <ExpanderContent>
                <EventFields type="birth" />
                <EventFields type="death" />
              </ExpanderContent>
            </Expander>
            <Separator size="xxs" />

            <Grid>
              <Cell columns={isMobile ? 'hide' : 2} />
              <Button
                columns={isMobile ? 12 : 10}
                fullWidth={isMobile}
                data-testid="show-more-less-button"
                linkName={open ? 'Less Search Options' : 'More Search Options'}
                onClick={(event) => {
                  setOpen(!open)
                  event.target.blur()
                }}
                Icon={ArrowCaret}
                iconDirection={open ? 'up' : 'down'}
              >
                {open ? t('ancestors.less-search-options.action') : t('ancestors.more-search-options.action')}
              </Button>
              {isMobile && <SearchButton data-testid="search-button" gender={gender} />}
            </Grid>
          </>
        )}
      </ZionForm>
    </div>
  )
}

function NameFields({ swapNames, onSearchPage = false, gender }): JSX.Element {
  const [t] = useTranslation()
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ md: false, default: true })

  return (
    <Grid guttersY="none">
      <Cell columns={isMobile ? 12 : 2} />
      {swapNames ? (
        <FormTextField
          columns={isMobile ? 12 : 5}
          data-testid="search-surname"
          label={t('ancestors.surname.label')}
          name="self.surname.value"
        />
      ) : (
        <FormTextField
          columns={isMobile ? 12 : 5}
          data-testid="search-given-name"
          label={t('ancestors.given-name.label')}
          name="self.given.value"
        />
      )}
      {swapNames ? (
        <FormTextField
          columns={isMobile ? 12 : 5}
          data-testid="search-given-name"
          label={t('ancestors.given-name.label')}
          name="self.given.value"
        />
      ) : (
        <FormTextField
          columns={isMobile ? 12 : 5}
          data-testid="search-surname"
          label={t('ancestors.surname.label')}
          name="self.surname.value"
        />
      )}

      {/* Without Theme here, WhiteText in HomePageHeader makes the button white, which looks odd in this situation. Can't use BillboardButton because it is too tall. */}
      {!(isMobile && onSearchPage) && (
        <Theme alignY="nextToInput" columns={isMobile ? 12 : 2} theme="day" transparent>
          <SearchButton data-testid="search-button" gender={gender} />
        </Theme>
      )}
    </Grid>
  )
}
